import { type FormEvent, useMemo } from "react";
import { useCarSearch as useApacCarSearch } from "@apac/cars";
import { useBooleanFlag } from "@apac/feature-flags";
import { FeatureFlagNames } from "@hsbc/core-utilities";
import dayjs from "dayjs";

export const useCarSearch = (
  onSubmit?: (event?: FormEvent<HTMLFormElement>) => void
) => {
  const { context, handlers } = useApacCarSearch(onSubmit);

  const { pickupDateTime, dropoffDateTime } = context;
  const { handlePickupDateTimeChange, handleDropoffDateTimeChange } = handlers;

  const pickupTime = useMemo(() => {
    if (
      pickupDateTime?.getHours() != null &&
      pickupDateTime?.getMinutes() != null
    ) {
      return dayjs()
        .hour(pickupDateTime.getHours())
        .minute(pickupDateTime.getMinutes())
        .toDate();
    }
    return undefined;
  }, [pickupDateTime]);

  const dropoffTime = useMemo(() => {
    if (
      dropoffDateTime?.getHours() != null &&
      dropoffDateTime?.getMinutes() != null
    ) {
      return dayjs()
        .hour(dropoffDateTime.getHours())
        .minute(dropoffDateTime.getMinutes())
        .toDate();
    }
    return undefined;
  }, [dropoffDateTime]);

  // Current car search hook doesn't separate date and time.
  // 1. Have to set a default time otherwise will use default 12:00am, not idle to set midnight as default
  // 2. Have to remove time info when update date with existing time, otherwise will retrigger timepicker logic with npe error
  // TODO: Migrated to core/lib with separate time and date state.
  const handlePickupDateChange = (date: Date | undefined) => {
    if (pickupDateTime == null) {
      // set 10:00 AM as default pickup time
      date?.setHours(10, 0, 0, 0);
      handlePickupDateTimeChange(date);
    } else if (date == null) {
      handlePickupDateTimeChange(date);
    } else {
      // keep existing time and only change date
      const newDate = new Date(pickupDateTime);
      if (date && pickupDateTime) {
        newDate.setFullYear(
          date.getFullYear(),
          date.getMonth(),
          date.getDate()
        );
      }
      handlePickupDateTimeChange(newDate);
    }
  };

  const handleDropoffDateChange = (date: Date | undefined) => {
    if (dropoffDateTime == null) {
      // set 10:00 AM as default drop off time
      date?.setHours(10, 0, 0, 0);
      handleDropoffDateTimeChange(date);
    } else if (date == null) {
      handleDropoffDateTimeChange(date);
    } else {
      // keep existing time and only change date
      const newDate = new Date(dropoffDateTime);
      if (date && dropoffDateTime) {
        newDate.setFullYear(
          date.getFullYear(),
          date.getMonth(),
          date.getDate()
        );
      }
      handleDropoffDateTimeChange(newDate);
    }
  };

  const handlePickupTimeChange = (time: string) => {
    // same with date change, here, update time only
    const newTime = dayjs(time);
    if (
      newTime.isValid() &&
      newTime?.hour() != null &&
      newTime?.minute() != null
    ) {
      handlePickupDateTimeChange(
        dayjs(pickupDateTime)
          .hour(dayjs(time).hour())
          .minute(dayjs(time).minute())
          .toDate()
      );
    }
  };

  const handleDropoffTimeChange = (time: string) => {
    const newTime = dayjs(time);
    if (
      newTime.isValid() &&
      newTime?.hour() != null &&
      newTime?.minute() != null
    ) {
      handleDropoffDateTimeChange(
        dayjs(
          dayjs(dropoffDateTime)
            .hour(dayjs(time).hour())
            .minute(dayjs(time).minute())
            .toDate()
        ).toDate()
      );
    }
  };

  const enableBPG = useBooleanFlag(FeatureFlagNames.BEST_PRICE_GUARANTEE);

  return {
    context: {
      ...context,
      pickupTime,
      dropoffTime,
      pickupDate: pickupDateTime,
      dropoffDate: dropoffDateTime,
      enableBPG,
    },
    handlers: {
      ...handlers,
      handlePickupDateChange,
      handleDropoffDateChange,
      handlePickupTimeChange,
      handleDropoffTimeChange,
    },
  };
};
